import React, { useEffect, useRef, useState } from 'react';
import useScreenWidth from '../../hooks/useScreenWidth';
import AutoComplete from '../AutoComplete/AutoCompleteSelect';
import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import clientUtils from '../../Utils/clientUtils';
import { Controller, useFormContext } from 'react-hook-form';
import { ApiHelper } from '../../common/ApiHelper';
import { uploadFile } from '../../common/Upload';
import Loader from '../Loader';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { RequestTabGroup } from './ExternalApiEngine';
import { DataExtractorConfig, ExternalApiCallMethod, InferenceOptions } from '../../models/APIModels';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ContentPaste from '@mui/icons-material/ContentPaste';
import InputIcon from '@mui/icons-material/Input';
import { NestedMenuItem, IconMenuItem } from 'mui-nested-menu';
import InterfaceType from './InterfaceType';
import { InterfaceTypes } from '../../enums';
import SettingsIcon from '@mui/icons-material/Settings';

import { EngineTypes } from '../../constants';
import InferenceSettings from './InferenceSettings';
import Progress from '../Loaders/Progress';

interface IProps {
  formValues: any;
}

const requestMethods = [
  {
    id: 'get',
    method: 'GET',
  },
  {
    id: 'post',
    method: 'POST',
  },
];

export const generateEmptyInferenceOptions = (): InferenceOptions => ({
  tPrefix: '%%<',
  tSuffix: '>%%',
  headers: {},
  jsonBodyTemplate: '',
  url: '',
  httpMethod: ExternalApiCallMethod.Post,
  outputExtractors: {},
});

const EngineConfigurationPanel = ({ formValues }: IProps) => {
  // ▶️ State Variables .....

  const [selectPredictEngineType, setSelectPredictEngineType] = useState('');
  const [selectRequestType, setSelectRequestTypeType] = useState(requestMethods[0]);
  const widthSize = useScreenWidth();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [isUploading, setUploading] = useState(false);
  const [isModelUploaded, setIsModelUploaded] = useState(false);
  const [selectedContextMenuFiled, setSelectedContextMenuFiled] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [showPasteOption, setShowPasteOption] = useState(false);
  const urlRef = useRef();
  const inputRefDetail = useRef();
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [appEngines, setAppEngines] = useState([]);
  const [selectedAppEngine, setSelectedAppEngine] = useState('');
  const [openInferenceSettingsDialog, setOpenInferenceSettingsDialog] = useState(false);

  const {
    register,
    getValues,
    setValue,
    control,
    formState: { errors },
    reset,
    watch,
  } = useFormContext();

  const watchInterfaceType = watch('interfaceType', 'Form');
  // ▶️ Functions/Methods .....

  const getInferenceOptions = () => {
    const inferenceOptions = getValues('aiEngineConfig.inferenceOptions');
    // console.log('inferenceOptions', inferenceOptions);
    if (inferenceOptions === null || Object.keys(inferenceOptions).length === 0) {
      const inferenceOptions = generateEmptyInferenceOptions();
      setValue('aiEngineConfig.inferenceOptions', inferenceOptions);
    }
    let outputExtractors = getValues('aiEngineConfig.inferenceOptions.outputExtractors') ?? {};
    const outcomes = getValues('aiEngineConfig.outcomes');
    for (let i = 0; i < outcomes.length; i++) {
      const outputExtractor = outputExtractors[outcomes[i].name];
      const stringExtractor = outputExtractor && outputExtractor['stringExtractor'] ? outputExtractor['stringExtractor'] : null;
      outputExtractors[outcomes[i].name] = {
        stringExtractor: {
          regEx: stringExtractor && stringExtractor['regEx'] ? stringExtractor['regEx'] : '',
          matchIndex: stringExtractor && stringExtractor['matchIndex'] >= 0 ? stringExtractor['matchIndex'] : 0,
          groupIndex: stringExtractor && stringExtractor['groupIndex'] >= 0 ? stringExtractor['groupIndex'] : -1,
        },
        binaryExtractor: null,
      };
    }
    const outputExtractorsKeys = Object.keys(outputExtractors);
    // removing all the key which does not exists  to outcomes
    for (let i = 0; i < outputExtractorsKeys.length; i++) {
      const outcome = outcomes.find((o) => o.name === outputExtractorsKeys[i]);
      if (!outcome) {
        delete outputExtractors[outputExtractorsKeys[i]];
      }
    }
    setValue('aiEngineConfig.inferenceOptions.outputExtractors', outputExtractors);
  };
  const handleEngineType = (value) => {
    if (value) {
      setValue('aiEngineConfig.predictEngineType', value.name);
      setSelectPredictEngineType(value);
      if (value.name === 'ExternalAIAPI') {
        getInferenceOptions();
      } else {
        setValue('aiEngineConfig.inferenceOptions', {});
      }
    }
  };
  const handleRequestType = (value) => {
    // console.log('value', value);
    if (value) {
      setValue('aiEngineConfig.inferenceOptions.httpMethod', value.name);
      setSelectRequestTypeType(value);
    }
  };

  const onBrowseButtonClick = () => {
    // `current` points to the mounted file input element
    if (fileInput && fileInput.current) {
      fileInput.current.click();
    }
  };

  const processFileChange = async (fileList: FileList) => {
    if (fileList.length === 0) return;

    const file = fileList.item(0);
    // console.log('file', file);
    await uploadModelFile(file);
    if (fileInput && fileInput.current) fileInput.current.value = '';
  };

  const uploadModelFile = async (file) => {
    setUploading(true);
    try {
      const uploadLinkInfoResponse = await ApiHelper.GetUploadLinkInfoForMAINMODEL(getValues('modelId'));
      // console.log('uploadLinkInfoResponse', uploadLinkInfoResponse);
      if (uploadLinkInfoResponse && uploadLinkInfoResponse.data && file) {
        const url = uploadLinkInfoResponse.data.publicUploadLink;
        const uploadResponse = await uploadFile(file, url);
        const modelData = getValues('modelData');
        if (modelData && modelData.filePaths) {
          modelData.filePaths = {
            ...modelData.filePaths,
            MAINMODEL: uploadLinkInfoResponse.data.destinationPath,
          };
          setValue('modelData', modelData);
        }
        setUploading(false);
      }
    } catch (e) {
      console.error('Unable to upload file', e);
      setUploading(false);
    }
  };

  const handleContextMenu = async (event: React.MouseEvent, fieldName, ref = null) => {
    // console.log('fieldName', fieldName);

    if (fieldName) {
      setSelectedContextMenuFiled(fieldName);
      inputRefDetail.current = ref ? ref.current : null;
    }
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null
    );
    let copyTextAvailable = await navigator.clipboard.readText();
    if (copyTextAvailable !== 'copyTextAvailable') {
      setShowPasteOption(true);
    } else {
      setShowPasteOption(false);
    }
  };

  const handleClose = (e) => {
    setContextMenu(null);
  };
  const handleClickOnContextMenuItem = async (e, type, name) => {
    //  setContextMenu(null);
    // console.log('handleClickOnContextMenuItem', { e, name, selectedContextMenuFiled });
    if (selectedContextMenuFiled && type === 'input') {
      if (selectedContextMenuFiled.input === 'headers') {
        const headers = getValues(selectedContextMenuFiled.fieldName);
        // console.log('headers', headers);
      } else {
        if (inputRefDetail && inputRefDetail.current) {
          let cursorPosition = inputRefDetail.current.selectionStart;
          let textBeforeCursorPosition = inputRefDetail.current.value.substring(0, cursorPosition);
          let textAfterCursorPosition = inputRefDetail.current.value.substring(
            cursorPosition,
            inputRefDetail.current.value.length
          );

          const tPrefix = getValues('aiEngineConfig.inferenceOptions.tPrefix');
          const tSuffix = getValues('aiEngineConfig.inferenceOptions.tSuffix');
          const textToInsert = `${tPrefix}${name}${tSuffix}`;
          // console.log(
          //   ' textBeforeCursorPosition + textToInsert + textAfterCursorPosition',
          //   textBeforeCursorPosition + textToInsert + textAfterCursorPosition
          // );
          const newValue = textBeforeCursorPosition + textToInsert + textAfterCursorPosition;
          setValue(selectedContextMenuFiled.fieldName, newValue);
          // Set the cursor position to after the newly inserted text
          inputRefDetail.current.selectionStart = inputRefDetail.current.selectionEnd = cursorPosition + textToInsert.length;
        }
        // const value = getValues(selectedContextMenuFiled.fieldName);
      }
      // setContextMenu(null);
    } else if (selectedContextMenuFiled && type === 'copy' && selectedContextMenuFiled.selectedText.length > 0) {
      handleCopyClick(selectedContextMenuFiled.selectedText);
      setSelectedText('');
    } else if (type === 'paste') {
      if (inputRefDetail && inputRefDetail.current) {
        let cursorPosition = inputRefDetail.current.selectionStart;
        let textBeforeCursorPosition = inputRefDetail.current.value.substring(0, cursorPosition);
        let textAfterCursorPosition = inputRefDetail.current.value.substring(cursorPosition, inputRefDetail.current.value.length);
        let textToInsert = await navigator.clipboard.readText();
        const newValue = textBeforeCursorPosition + textToInsert + textAfterCursorPosition;
        setValue(selectedContextMenuFiled.fieldName, newValue);
        // Set the cursor position to after the newly inserted text
        inputRefDetail.current.selectionStart = inputRefDetail.current.selectionEnd = cursorPosition + textToInsert.length;
        // setIsCopied(false);
        setShowPasteOption(false);
      }
    }
    setContextMenu(null);
  };

  const handleMouseUp = () => {
    // console.log(`Selected text: ${window.getSelection().toString()}`);
    if (window) {
      setSelectedText(window.getSelection()?.toString());
    }
  };

  const copyTextToClipboard = async (text) => {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  };
  const handleCopyClick = (copyText) => {
    // Asynchronously call copyTextToClipboard
    copyTextToClipboard(copyText)
      .then(() => {
        // If successful, update the isCopied state value
        // setIsCopied(true);
        setTimeout(() => {
          // setIsCopied(false);
        }, 1500);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handlePaste = async (e) => {
    let text = await navigator.clipboard.readText();
    let cursorPosition = e.target.selectionStart;
    // console.log({ text, cursorPosition });
  };

  const getAppEngines = async () => {
    const response = await ApiHelper.GetUserEngines();
    // console.log('response', response);
    if (response && response.data) {
      const appEngineId = getValues('appEngineId');
      setAppEngines(response.data);
      if (appEngineId) {
        const appEngine = response.data.find((engine) => engine.engineId === appEngineId);
        setSelectedAppEngine(appEngine);
      }
    }
  };

  // ▶️ Hooks .....

  useEffect(() => {
    const mainModel = getValues('modelData.filePaths.MAINMODEL');
    let predictEngineType = getValues('aiEngineConfig.predictEngineType');
    const engineType = EngineTypes.find((e) => e.name === predictEngineType);
    const requestMethod = requestMethods.find((r) => r.method === getValues('aiEngineConfig.inferenceOptions.httpMethod'));

    if (engineType) {
      setSelectPredictEngineType(engineType);
    }
    // console.log('predictEngineType', predictEngineType)
    if (predictEngineType === 'ExternalAIAPI') {
      getInferenceOptions();
    }
    if (mainModel) {
      setIsModelUploaded(true);
    }
    // console.log(requestMethod, getValues('aiEngineConfig.inferenceOptions.headers'));
    if (requestMethod) {
      setSelectRequestTypeType(requestMethod);
    }
    getAppEngines();

    return () => {
      console.log('cleaned up');
    };
  }, []);
  useEffect(() => {
    // console.log('outcomes changed', getValues('aiEngineConfig.outcomes'));
    let predictEngineType = getValues('aiEngineConfig.predictEngineType');
    if (predictEngineType === 'ExternalAIAPI') {
      getInferenceOptions();
    } else {
      setValue('aiEngineConfig.inferenceOptions', {});
    }
  }, [getValues('aiEngineConfig.outcomes')]);

  return (
    <Grid container spacing={3}>
      {isUploading && <Progress  progressLabel='Please Wait ...'/>}
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={5}
        xl={4}
        style={clientUtils.getGridStyle('left', widthSize, 1200)}
        sx={{
          minHeight: '180px',
          height: selectPredictEngineType && selectPredictEngineType.name === 'AppEngine' ? '250px' : '100px',
        }}
        container
        spacing={2}
      >
        <Grid item xs={4} sm={6} md={7} lg={6}>
          <AutoComplete
            id="engine-type-select"
            items={EngineTypes}
            itemText="label"
            itemKey="id"
            labelText="AI Engine Type"
            placeHolderText="Select Engine Type"
            multiSelect={false}
            onChange={handleEngineType}
            defaultValue={selectPredictEngineType}
          />
        </Grid>

        <Grid item xs={8} sm={6} md={5} lg={6} sx={{ textAlign: 'right' }}>
          <input
            className="file-input"
            ref={fileInput}
            id="uploadModelFile"
            type="file"
            onChange={(e) => processFileChange(e.target.files)}
          />
          <Button
            size="small"
            fullWidth={true}
            variant="contained"
            sx={{
              fontSize: { xs: '12px', sm: '12px', md: '12px', lg: '14px', xl: '14px' },
              width: { xs: '240px', sm: '200px', md: '250px', lg: '180px', xl: '200px' },
            }}
            disabled={
              selectPredictEngineType &&
              (selectPredictEngineType.name === 'ExternalAIAPI' || selectPredictEngineType.name === 'AppEngine')
                ? true
                : false
            }
            onClick={onBrowseButtonClick}
            endIcon={isModelUploaded ? <CheckCircleOutlineRoundedIcon /> : null}
          >
            {isModelUploaded ? 'Update Model File' : 'Upload Model File'}
          </Button>
          {/* {isModelUploaded && <CheckCircleOutlineRoundedIcon  color='success' sx={{paddingLeft:1, paddingBottom:0.3, fontSize:'30px'}}/>} */}
        </Grid>

        {selectPredictEngineType && selectPredictEngineType.name === 'AppEngine' && (
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <AutoComplete
              id="app-engine-type-select"
              items={appEngines}
              itemText="engineName"
              itemKey="engineId"
              labelText="App Engine"
              placeHolderText="Select Engine"
              multiSelect={false}
              onChange={(value) => {
                setSelectedAppEngine(value);
                setValue('appEngineId', value.engineId);
              }}
              defaultValue={selectedAppEngine}
            />
          </Grid>
        )}
        <Grid item xs={watchInterfaceType === InterfaceTypes.Chatbot ? 11 : 12}>
          <InterfaceType />
        </Grid>
        {watchInterfaceType === InterfaceTypes.Chatbot && (
          <Grid item xs={1} alignContent={'center'} justifyContent='right'>
            <SettingsIcon sx={{ cursor: 'pointer' }} titleAccess='Display Settings' onClick={() => setOpenInferenceSettingsDialog(true)}/>
          </Grid>
          
        )}
        {openInferenceSettingsDialog && <InferenceSettings open={openInferenceSettingsDialog} close={() => setOpenInferenceSettingsDialog(false)}/>}
        <Grid container item xs={12}>
          {/* ## Long run infrence ## */}
          {watchInterfaceType === InterfaceTypes.Form && <Grid item xs={12}>
            <Controller
              name="longRunningInference"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  // sx={{ ml: 0 }}
                  control={<Switch size="small" checked={value} onChange={onChange} />}
                  label="Long Running Inference"
                  labelPlacement="end"
                />
              )}
            />
          </Grid>}

          {/* ## Need Context ## */}
          <Grid item xs={12}>
            <Controller
              name="needsContext"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  // sx={{ ml: 0 }}
                  control={<Switch size="small" checked={value} onChange={onChange} />}
                  label="Need Context"
                  labelPlacement="end"
                />
              )}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* ## Long run infrence && Need Context && Enable chatbot ## */}

      {/* ## Infrence Options ## */}
      <Grid item xs={12} sm={12} md={12} lg={7} xl={8} style={clientUtils.getGridStyle('right', widthSize, 1300)} container>
        <Grid item xs={12}>
          <Box className="hedading">
            <div className="title">Inference Options</div>
            <div className="line"></div>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {selectPredictEngineType && selectPredictEngineType.name !== 'ExternalAIAPI' && <p>Nothing to configure here!</p>}
        </Grid>
        <Grid item xs={12}>
          {selectPredictEngineType && selectPredictEngineType.name === 'ExternalAIAPI' && (
            <p>
              Please define how http call will be made to the external service. Right click on any text area to insert template
              variables.
            </p>
          )}
        </Grid>
        {selectPredictEngineType && selectPredictEngineType.name === 'ExternalAIAPI' && (
          <>
            <Grid item xs={4}>
              <AutoComplete
                id="request-type-select"
                items={requestMethods}
                itemText="method"
                itemKey="id"
                labelText="Request Type"
                placeHolderText="Select Request Type"
                multiSelect={false}
                onChange={handleRequestType}
                defaultValue={selectRequestType}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                id="name"
                label="Url"
                variant="outlined"
                placeholder="Enter URL"
                size="small"
                fullWidth
                // onSelect={updateSelectionStart}
                inputRef={urlRef}
                onMouseUp={handleMouseUp}
                onPaste={handlePaste}
                {...register('aiEngineConfig.inferenceOptions.url')}
                defaultValue={getValues('aiEngineConfig.inferenceOptions.url') ?? ''}
                onContextMenu={(e) =>
                  handleContextMenu(
                    e,
                    { input: 'url', fieldName: 'aiEngineConfig.inferenceOptions.url', selectedText: selectedText },
                    urlRef
                  )
                }
                inputProps={{
                  onFocus: (e) => {
                    // e.currentTarget.setSelectionRange(0, 0);
                    setTimeout(() => {
                      // console.log("onFocus within timout", urlRef.current.selectionStart);
                      if (urlRef.current) {
                        // urlRef.current.setSelectionRange(0, 10);
                      }
                    }, 1000);
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ pt: 2 }}>
              <RequestTabGroup handleContextMenu={(e, fieldName, inputRef) => handleContextMenu(e, fieldName, inputRef)} />
            </Grid>
            {/* <Grid item xs={2}>
              <Button size="small" fullWidth={true} variant="contained">
                Send
              </Button>
            </Grid> */}
            <Paper>
              <Menu
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ padding: '0px !important', marginTop: '20px' }}
                className="menu-custom"
              >
                <IconMenuItem
                  onClick={(e) => handleClickOnContextMenuItem(e, 'copy', '')}
                  leftIcon={
                    <ListItemIcon>
                      <ContentCopy fontSize="small" />
                    </ListItemIcon>
                  }
                  rightIcon={
                    <Typography variant="body2" color="text.secondary">
                      ⌘C
                    </Typography>
                  }
                  label="Copy"
                />
                {showPasteOption && (
                  <IconMenuItem
                    onClick={(e) => handleClickOnContextMenuItem(e, 'paste', '')}
                    leftIcon={
                      <ListItemIcon>
                        <ContentPaste fontSize="small" />
                      </ListItemIcon>
                    }
                    rightIcon={
                      <Typography variant="body2" color="text.secondary">
                        ⌘V
                      </Typography>
                    }
                    label="Paste"
                  />
                )}

                <Divider />
                <NestedMenuItem
                  leftIcon={
                    <ListItemIcon>
                      <InputIcon fontSize="small" />
                    </ListItemIcon>
                  }
                  // rightIcon={<ContentCopy />}
                  label="Insert Input Variable"
                  parentMenuOpen={contextMenu !== null}
                >
                  {getValues('aiEngineConfig.inputs') &&
                    getValues('aiEngineConfig.inputs').map((i) => (
                      <MenuItem
                        sx={{ paddingTop: '0px', paddingBottom: 0 }}
                        onClick={(e) => handleClickOnContextMenuItem(e, 'input', i.name)}
                        key={i.name}
                      >
                        {i.name}
                      </MenuItem>
                    ))}
                </NestedMenuItem>
              </Menu>
            </Paper>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default EngineConfigurationPanel;
